import { useEffect, useRef } from 'react';

import { Note } from 'src/services/api/notesApi';

import { NotesListItem } from './NotesListItem';

export const NotesList = ({
  notes,
  onClick,
  pendingUpdateIds,
}: {
  notes: Note[];
  onClick: (note: Note) => void;
  pendingUpdateIds: ReadonlySet<string>;
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const prevTopIdRef = useRef<string | null>(null);

  // When an optimistic new note appears at the top (temp- id), scroll it into
  // view — but only if the user isn't already at the top. Without this, a
  // user scrolled to the bottom of the list won't see their freshly-created
  // note. Skipping when already at the top prevents a tiny unwanted nudge.
  useEffect(() => {
    const topId = notes.at(0)?.id ?? null;
    const prevTopId = prevTopIdRef.current;
    prevTopIdRef.current = topId;
    if (!topId || topId === prevTopId || !topId.startsWith('temp-')) return;
    let scrollable: HTMLElement | null = listRef.current?.parentElement ?? null;
    while (scrollable) {
      const { overflowY } = getComputedStyle(scrollable);
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        scrollable.scrollHeight > scrollable.clientHeight
      ) {
        break;
      }
      scrollable = scrollable.parentElement;
    }
    if (!scrollable || scrollable.scrollTop <= 2) return;
    scrollable.scrollTo({ top: 0, behavior: 'smooth' });
  }, [notes]);

  return (
    <div ref={listRef} className="w-full">
      {notes.map((note) => (
        <NotesListItem
          key={note.id}
          note={note}
          isUpdating={pendingUpdateIds.has(note.id)}
          onClick={() => onClick(note)}
        />
      ))}
    </div>
  );
};
